<template>
  <div class="vcv-tags">
    <vpreloader v-if="isFetchingTags" size="24" />

    <div v-if="!isFetchingTags" class="vcv-tags__list">
      <template v-if="responseTags.length > 0">
        <vcv-tags-item
          v-for="item in responseTags"
          :key="item.id"
          :text="item._embedded.tag.name"
          :disabled="isEditingTags"
          is-removeble
          @remove="removeResponseTag(item.id)"
        />
      </template>

      <div class="vcv-tags__add">
        <div class="vcv-tags__add-input">
          <input
            v-model="newResponseTag"
            :placeholder="$tr('vcv_tags.new')"
            :disabled="isAddingTag"
            type="text"
            @input="fetchResponseTagsSuggestions($event.target.value)"
            @keyup.enter="addResponseTag"
          />

          <div
            class="vcv-tags__add-input-icon"
            :class="{ disabled: newResponseTag.length === 0 || isAddingTag || isFetchingTags || isEditingTags }"
            @click="addResponseTag"
          >
            ↵
          </div>

          <transition name="slide-fade">
            <div
              v-show="responseTagsSuggestions.length > 0"
              v-on-click-away="hideSuggestions"
              class="vcv-tags__suggestions"
              :class="{ 'vcv-tags__suggestions--double': maxLengthOfLongestWord > 16 }"
            >
              <div v-for="item in responseTagsSuggestions" :key="item.id">
                <div class="vcv-tags__suggestions-item" @click="onSelectTag(item)">
                  <span v-html="item.name"></span>
                </div>
              </div>
            </div>
          </transition>
        </div>

        <vpreloader v-if="isFetchingSuggestions" size="24" class="vcv-tags__add-loader" />
      </div>
    </div>

    <i v-if="isCompanyAdmin" class="vcv-tags__settings-btn icon-gear" @click="isShowSettings = true"></i>

    <vcv-tags-settings
      :is-show-settings="isShowSettings"
      @close="isShowSettings = false"
      @fetch-response-tags="fetchResponseTags"
    />
  </div>
</template>

<script>
import Axios from 'axios';
import qs from 'qs';
import debounce from 'lodash/debounce';
import { mapState, mapGetters } from 'vuex';
import { directive as VueClickAway } from 'vue-clickaway';
import VcvTagsSettings from '@/components/response/VcvTagsSettings.vue';
import VcvTagsItem from '@/components/response/VcvTagsItem.vue';

export default {
  components: {
    VcvTagsSettings,
    VcvTagsItem
  },

  directives: {
    onClickAway: VueClickAway
  },

  data() {
    return {
      isFetchingTags: false,
      isEditingTags: false,
      responseTags: [],
      isAddingTag: false,
      newResponseTag: '',
      isFetchingSuggestions: false,
      responseTagsSuggestions: [],
      isShowSettings: false
    };
  },

  computed: {
    ...mapState('response', ['response']),

    ...mapGetters('user', ['isCompanyAdmin']),

    maxLengthOfLongestWord() {
      let maxWordLength = 0;

      this.responseTagsSuggestions.forEach(x => {
        let strokeWithoutTags = x.name.replace(/(<([^>]+)>)/gi, '');
        strokeWithoutTags = strokeWithoutTags.split(' ');
        let longestWord = 0;

        for (let i = 0; i < strokeWithoutTags.length; i++) {
          if (strokeWithoutTags[i].length > longestWord) {
            longestWord = strokeWithoutTags[i].length;
          }
        }

        if (longestWord > maxWordLength) {
          maxWordLength = longestWord;
        }
      });

      return maxWordLength;
    }
  },

  async created() {
    await this.fetchResponseTags();
  },

  methods: {
    async fetchResponseTags() {
      this.isFetchingTags = true;
      const params = { page: 1, limit: 9999, with: ['tag'], filter: { response_id: this.response.id } };

      await Axios.get('/v3/response-tags', { params, paramsSerializer: params => qs.stringify(params) })
        .catch(err => {
          this.$store.dispatch('global/setError', err, { root: true });
        })
        .then(response => {
          this.responseTags = response.data._embedded.response_tags;
        })
        .finally(() => {
          this.isFetchingTags = false;
        });
    },

    async removeResponseTag(responseTagId) {
      this.isEditingTags = true;

      await Axios.delete(`/v3/response-tags/${responseTagId}`)
        .catch(err => {
          this.$store.dispatch('global/setError', err, { root: true });
        })
        .then(response => {
          if (response.status === 200) {
            const tagIndex = this.responseTags.findIndex(x => x.id === responseTagId);

            if (tagIndex >= 0) {
              this.responseTags.splice(tagIndex, 1);
            }
          }
        })
        .finally(() => {
          this.isEditingTags = false;
        });
    },

    fetchResponseTagsSuggestions: debounce(async function (searchWord) {
      if (searchWord.length > 0) {
        this.isFetchingSuggestions = true;

        const params = { page: 1, limit: 9999, with: ['tag'], filter: { name: searchWord } };

        await Axios.get('/v3/tags', { params, paramsSerializer: params => qs.stringify(params) })
          .catch(err => {
            this.$store.dispatch('global/setError', err, { root: true });
          })
          .then(response => {
            if (response.data._embedded.tags.length > 0) {
              const existTagsIds = this.responseTags.map(x => x.tag_id);
              const suggestWithoutExistTags = response.data._embedded.tags
                .filter(x => !existTagsIds.includes(x.id))
                .map(x => {
                  return {
                    ...x,
                    name: x.name.replace(new RegExp(`${searchWord}`, 'gi'), `<strong>${searchWord}</strong>`)
                  };
                });

              this.responseTagsSuggestions = suggestWithoutExistTags;
            } else {
              this.responseTagsSuggestions = [];
            }
          })
          .finally(() => {
            this.isFetchingSuggestions = false;
          });
      } else {
        this.responseTagsSuggestions = [];
      }
    }, 500),

    async onSelectTag(tag) {
      this.isEditingTags = true;
      const tagId = tag.id;

      const params = { response_id: this.response.id, with: ['tag'] };
      if (tagId !== undefined) {
        params.tag_id = tagId;
      }
      const newParams = qs.stringify(params);

      await Axios.post('/v3/response-tags', newParams)
        .catch(err => {
          this.$store.dispatch('global/setError', err, { root: true });
        })
        .then(async response => {
          if (response.status === 200) {
            const newTag = response.data;

            if (tag !== undefined) {
              newTag._embedded = {};
              newTag._embedded.tag = tag;
              newTag._embedded.tag.name = newTag._embedded.tag.name.replace('<strong>', '');
              newTag._embedded.tag.name = newTag._embedded.tag.name.replace('</strong>', '');
            }

            this.responseTags.push(newTag);
            this.newResponseTag = '';
            this.responseTagsSuggestions = [];
          }
        })
        .finally(() => {
          this.isEditingTags = false;
        });
    },

    async addResponseTag() {
      const tagExist = this.responseTags.filter(
        x => x._embedded.tag.name.toLowerCase() === this.newResponseTag.toLowerCase()
      );

      if (tagExist.length === 0) {
        this.isAddingTag = true;
        this.isFetchingSuggestions = true;

        await Axios.post('/v3/tags', { name: this.newResponseTag })
          .catch(err => {
            this.$store.dispatch('global/setError', err, { root: true });
          })
          .then(async response => {
            if (response.status === 200) {
              await this.onSelectTag(response.data);
            }
          })
          .finally(() => {
            this.isAddingTag = false;
            this.isFetchingSuggestions = false;
          });
      } else {
        this.$noty.error(this.$tr('responses.sendMessage.warnings.alreadyExists'));
      }
    },

    hideSuggestions() {
      this.responseTagsSuggestions = [];
      this.newResponseTag = '';
    }
  }
};
</script>

<style lang="scss" scoped>
.vcv-tags {
  border-top: 1px solid $grey-lighter;
  padding: 15px 0 320px;
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  &__add {
    display: flex;
  }

  &__add-input {
    border-radius: 4px;
    color: $grey;
    display: flex;
    max-width: 184px;
    height: 32px;
    border: 1px solid $grey-light;
    position: relative;

    input:not(.multiselect__input) {
      border: 0;
      background: $grey-lighter;
      color: $main-dark;
      height: 100% !important;
      line-height: 1;
      padding: 0 25px 0 10px;
      width: 100%;
      border: 1px solid transparent;
      transition: border 0.3s;

      &:focus {
        border: 1px solid $green;
        transition: border 0.3s;
      }
    }
  }

  &__add-input-icon {
    position: absolute;
    top: 0;
    right: 0;
    width: 26px;
    font-size: 14px;
    margin-top: 4px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: all;
    cursor: pointer;
    transition: color 0.3s;

    &:hover {
      color: $green-hover;
      transition: color 0.3s;
    }

    &.disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  &__add-loader {
    margin-left: 10px;
    margin-top: 3px;
  }

  &__list {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    padding-right: 30px;
  }

  &__suggestions {
    position: absolute;
    top: 32px;
    left: 0;
    background: $white;
    box-shadow: $box-shadow;
    border-radius: 2px;
    max-height: 300px;
    overflow-y: auto;
    width: 100%;
    padding: 8px 0;

    > div {
      .vcv-tags__suggestions-item {
        border-bottom: 1px solid $grey-light;
      }

      &:last-child {
        .vcv-tags__suggestions-item {
          border-bottom: none;
        }
      }
    }

    &--double {
      width: 200%;
    }
  }

  &__suggestions-item {
    cursor: pointer;
    color: $main-dark;
    padding: 6px 10px;
    background: $white;
    font-size: 14px;
    max-width: 100%;
    transition: background 0.3s;

    &:hover {
      background: $grey-lighter;
      transition: background 0.3s;
    }
  }

  &__settings-btn {
    position: absolute;
    top: 15px;
    right: 0;
    height: 32px;
    width: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    color: $grey;
    cursor: pointer;
    transition: color 0.3s;

    &:hover {
      color: $green-hover;
      transition: color 0.3s;
    }
  }
}
</style>
